import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ENABLED_KEY = 'learningMode';
const COLLAPSED_KEY = 'learningModeCollapsed';

interface LearningModeContextType {
  /** Be van-e kapcsolva a tanuló mód: modul útmutatók és ? jelek a gombok mellett. */
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  toggle: () => void;
  /** Becsukta-e a felhasználó egy modul útmutatóját. Csukva egy sorra zsugorodik, nem tűnik el. */
  isCollapsed: (moduleId: string) => boolean;
  setCollapsed: (moduleId: string, collapsed: boolean) => void;
}

const LearningModeContext = createContext<LearningModeContextType | undefined>(undefined);

// Privát ablakban vagy tiltott tárhelynél a localStorage dobhat - ilyenkor
// a tanuló mód egyszerűen kikapcsolva indul.
const read = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
};

const write = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* nem baj, ha nem marad meg */
  }
};

/**
 * A tanuló mód eszközönkénti beállítás: aki a telefonján tanulja az appot,
 * a gépén nem feltétlenül akarja látni - ezért localStorage, nem profil.
 */
export const LearningModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabledState] = useState<boolean>(() => read(ENABLED_KEY, false));
  const [collapsed, setCollapsedState] = useState<string[]>(() => read(COLLAPSED_KEY, []));

  useEffect(() => write(ENABLED_KEY, enabled), [enabled]);
  useEffect(() => write(COLLAPSED_KEY, collapsed), [collapsed]);

  const setEnabled = useCallback((value: boolean) => setEnabledState(value), []);
  const toggle = useCallback(() => setEnabledState((v) => !v), []);
  const isCollapsed = useCallback((id: string) => collapsed.includes(id), [collapsed]);
  const setCollapsed = useCallback((id: string, value: boolean) => {
    setCollapsedState((prev) => (value ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  }, []);

  return (
    <LearningModeContext.Provider value={{ enabled, setEnabled, toggle, isCollapsed, setCollapsed }}>
      {children}
    </LearningModeContext.Provider>
  );
};

export const useLearningMode = () => {
  const context = useContext(LearningModeContext);
  if (!context) {
    throw new Error('useLearningMode must be used within a LearningModeProvider');
  }
  return context;
};
